/**
 * ═══════════════════════════════════════════════════════════════════════════
 * EL MODAL · una sola caja, para las dos aplicaciones
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Existe porque el 07/09/2026 hizo falta uno en la PÁGINA DE VENTA —el aviso
 * de que las suscripciones están cerradas, que el dueño pidió como modal— y
 * los dos que ya había vivían dentro de pantallas de la plataforma, con su
 * comportamiento escrito a mano cada uno.
 *
 * Escribir el tercero a mano habría sido lo rápido. Y sería el tercero de tres
 * que un día se cierran de tres maneras distintas: uno con Escape, otro no;
 * uno devolviendo el foco, otro dejándolo perdido en la página de detrás.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * LO QUE HACE, Y NINGUNA DE LAS CUATRO ES ADORNO
 *
 * 1 · **Escape cierra.** Es lo primero que prueba cualquiera que se sienta
 *     atrapado, y si no funciona la sensación de trampa se confirma.
 *
 * 2 · **Tocar fuera cierra**, y tocar dentro no. Sin el `stopPropagation` del
 *     medio, pulsar un botón del propio modal lo cierra debajo del dedo.
 *
 * 3 · **El foco entra al abrirse.** Sin esto, quien navega con teclado sigue
 *     con el foco en el botón de detrás: tabula por una página que ya no puede
 *     usar, sin enterarse de que hay algo delante.
 *
 * 4 · **Y VUELVE AL CERRARSE**, que es lo que ninguno de los dos anteriores
 *     hacía. Al cerrar, el foco se caía al principio del documento y había que
 *     volver a tabular desde arriba para llegar al botón que se acababa de
 *     pulsar.
 *
 *     ── Y ESTO COSTÓ DOS INTENTOS, LOS DOS MEDIDOS ────────────────────────
 *
 *     El primero guardaba `document.activeElement` al abrir y lo devolvía al
 *     cerrar. Medido: acababa en `<body>`. El segundo lo metió en un
 *     `queueMicrotask` por si React devolvía el foco después. Medido otra vez:
 *     `<body>` igual.
 *
 *     La causa no estaba aquí. **El botón que abre el modal se deshabilita
 *     mientras pide al servidor** —`disabled={ocupado}`— y un botón que se
 *     deshabilita PIERDE EL FOCO: el navegador lo manda a `<body>`. Así que
 *     para cuando este modal se monta, no queda nada a lo que volver. El
 *     código guardaba fielmente `<body>` y devolvía fielmente `<body>`.
 *
 *     Por eso hay `volverElFocoA`: quien abre el modal sabe a qué elemento hay
 *     que volver, y lo dice. `document.activeElement` se queda de reserva para
 *     los modales que se abren sin que nadie se deshabilite.
 *
 * 5 · **EL TABULADOR SE QUEDA DENTRO** (10/09). Aquí ponía que no, «dicho
 *     para que sea una decisión y no un descuido», y el precio era peor de lo
 *     que parecía: con `aria-modal` el lector de pantalla da el fondo por
 *     tapado, pero tabulando se salía a él, y como Escape se escucha en el
 *     propio diálogo, desde fuera ya no cerraba (cuestionario del hermano,
 *     P41). Ahora Tab en el último vuelve al primero y Mayús+Tab en el primero
 *     va al último. Se queda con `onKeyDown` y no con `<dialog>`: lo mismo en
 *     los tres navegadores sin cambiar cómo se pinta.
 *
 * 6 · **Y SI NO HAY ADÓNDE VOLVER, AL PRINCIPIO DEL CONTENIDO.** Un modal que
 *     se abre solo —al cargar, sin que nadie pulse nada— no tiene botón de
 *     origen: el foco estaba en `<body>`, y devolverlo ahí es mandar a quien
 *     usa teclado a tabular desde la cabecera. Va al `<main>`.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * LOS DOS QUE YA HABÍA, AQUÍ DESDE EL 10/09 · Y CÓMO SE MIRARON SIN CUENTA
 *
 * `MuroDePago` y `LaOfertaQueSeSalto` tenían su comportamiento escrito a mano
 * y ninguno devolvía el foco ni guardaba el tabulador. Esta cabecera pedía
 * verlos abiertos antes de tocarlos, y los dos piden una cuenta —el muro, ocho
 * sesiones gastadas; la oferta, un desbloqueo de soporte— que el agente que
 * los cambió no crea.
 *
 * Así que se montaron LOS DOS COMPONENTES DE VERDAD en una página temporal del
 * servidor de desarrollo —con este modal y con `StrictMode`, sin API y sin
 * cuenta— y se probaron en el navegador con el teclado de verdad:
 *
 *   · el muro ...... el foco entra; Tab da la vuelta por «Cerrar», «Tu cuenta»,
 *                    WhatsApp y el correo; Mayús+Tab la da al revés; Escape
 *                    cierra y el foco cae en el TITULAR DE LA TARJETA.
 *   · la oferta .... abierta SOLA, sin que nadie pulse: el foco entra; Tab da la
 *                    vuelta por «Cerrar», el campo del código, WhatsApp y el
 *                    correo —«Canjear» se salta mientras está deshabilitado—;
 *                    Escape cierra y el foco va al `<main>`.
 *
 * Y antes, este modal con el aviso de «todavía no se puede pagar»: ahí salió
 * el fallo de `StrictMode` que está explicado más abajo. La página temporal se
 * borró al acabar: no se commitea un banco de pruebas dentro de la aplicación.
 */

import { useCallback, useEffect, useRef } from "react";

export function Modal({
  titulo,
  etiquetaDeCerrar,
  onCerrar,
  volverElFocoA,
  children,
}: {
  /** El titular. Se usa también para nombrar el diálogo a los lectores. */
  readonly titulo: string;
  /** Qué se lee del aspa. Va traducido por quien monta el modal. */
  readonly etiquetaDeCerrar: string;
  readonly onCerrar: () => void;
  /**
   * Adónde vuelve el foco al cerrar, si quien abre el modal lo sabe.
   *
   * Hace falta cuando el botón que abre se deshabilita mientras pide al
   * servidor: para entonces el navegador ya se ha llevado el foco a `<body>` y
   * no queda nada que recordar. Ver la cabecera.
   */
  readonly volverElFocoA?: React.RefObject<HTMLElement | null> | undefined;
  readonly children: React.ReactNode;
}): React.ReactElement {
  const caja = useRef<HTMLDivElement>(null);
  /* Quién tenía el foco antes de abrir, para devolvérselo. Se lee en el mismo
     efecto que lo mueve: leerlo en el render daría el de un render anterior. */
  const deDonde = useRef<Element | null>(null);

  useEffect(() => {
    const nodo = caja.current;
    /* En el segundo montaje de `StrictMode` el foco ya está DENTRO: eso no es
       de dónde se vino, y se conserva el que se apuntó en el primero. */
    if (!nodo?.contains(document.activeElement)) deDonde.current = document.activeElement;
    nodo?.focus();
    return () => {
      /*
        EL QUE DIGA QUIEN ABRE MANDA, y `document.activeElement` es la reserva.
        El porqué está en la cabecera: un botón que se deshabilita mientras
        pide ya no tiene el foco cuando este modal aparece.

        La reserva se guarda AQUÍ y lo que diga quien abre se lee DENTRO de la
        microtarea (10/09): el muro manda el foco a la tarjeta que aparece al
        cerrarse, y esa tarjeta nace en el mismo cambio que desmonta esto. Leído
        aquí, su referencia podía estar todavía vacía.
      */
      const reserva = deDonde.current;
      /*
        ══════════════════════════════════════════════════════════════════════
        Y SE DEVUELVE EN UN MICROTAREA, NO A PELO
        ══════════════════════════════════════════════════════════════════════

        React ejecuta esta limpieza MIENTRAS todavía está borrando el árbol,
        con `.vc-modal` aún en el documento y aún con el foco. Un `focus()`
        aquí mismo corre antes de que el navegador quite ese nodo — y al quitar
        el elemento enfocado, el foco se va a `<body>` por su cuenta. La última
        palabra la tendría el navegador.

        `queueMicrotask` corre con la tarea ya terminada: el modal fuera, el
        foco ya caído, y entonces sí se recupera.

        `instanceof HTMLElement` y no un `as`: entre abrir y cerrar, el
        elemento puede haberse ido del documento —un modal que cierra la
        pantalla que lo abrió— y enfocar eso no lleva a ninguna parte.
      */
      queueMicrotask(() => {
        /*
          SI EL DIÁLOGO SIGUE EN LA PÁGINA, NO SE HA CERRADO (10/09).

          `StrictMode` monta cada efecto, lo limpia y lo vuelve a montar. La
          limpieza encolaba «devuelve el foco al botón», y esa microtarea corría
          DESPUÉS del segundo montaje, que ya había enfocado el diálogo: en
          desarrollo el foco no se quedaba nunca dentro. Medido en el navegador
          con el aviso de «todavía no se puede pagar». Una limpieza de verdad
          saca el diálogo del documento; si sigue conectado, era el ensayo de
          `StrictMode` y aquí no hay nada que devolver.
        */
        if (nodo?.isConnected) return;
        const vuelve = volverElFocoA?.current ?? reserva;
        if (vuelve instanceof HTMLElement && vuelve.isConnected && vuelve !== document.body) {
          vuelve.focus();
          return;
        }
        /* El 6 de la cabecera: sin botón de origen, al principio del contenido. */
        const principio = document.querySelector<HTMLElement>("main");
        if (principio) {
          if (!principio.hasAttribute("tabindex")) principio.setAttribute("tabindex", "-1");
          principio.focus({ preventScroll: true });
        }
      });
    };
  }, [volverElFocoA]);

  const alTeclear = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Escape") {
        onCerrar();
        return;
      }
      /* El 5 de la cabecera: el tabulador da la vuelta dentro. */
      if (e.key !== "Tab" || !caja.current) return;
      const enfocables = [
        ...caja.current.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), ' +
            'textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ),
      ];
      const primero = enfocables[0];
      const ultimo = enfocables[enfocables.length - 1];
      if (!primero || !ultimo) {
        e.preventDefault();
        return;
      }
      const activo = document.activeElement;
      if (e.shiftKey && (activo === primero || activo === caja.current)) {
        e.preventDefault();
        ultimo.focus();
      } else if (!e.shiftKey && activo === ultimo) {
        e.preventDefault();
        primero.focus();
      }
    },
    [onCerrar],
  );

  return (
    <div className="vc-velo" onClick={onCerrar}>
      <div
        className="vc-modal"
        role="dialog"
        aria-modal="true"
        aria-label={titulo}
        tabIndex={-1}
        ref={caja}
        onKeyDown={alTeclear}
        onClick={(e) => { e.stopPropagation(); }}
      >
        <button
          className="vc-modal__cerrar"
          type="button"
          onClick={onCerrar}
          aria-label={etiquetaDeCerrar}
        >
          ×
        </button>
        <h2>{titulo}</h2>
        {children}
      </div>
    </div>
  );
}
